/**
 * Group Store
 * ===========
 * State management for client groups (bulk assignment).
 */

import { create } from 'zustand'
import { groupService, type GroupWithMembers, type GroupFilters } from '../services'
import type { Group, GroupInsert, GroupUpdate } from '../types/database.types'

interface GroupState {
  // Data
  groups: (Group & { member_count: number })[]
  selectedGroup: GroupWithMembers | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: GroupFilters
  searchQuery: string

  // Actions
  fetchGroups: (providerId: string) => Promise<void>
  fetchGroupById: (groupId: string) => Promise<GroupWithMembers | null>
  createGroup: (group: GroupInsert) => Promise<Group>
  updateGroup: (groupId: string, updates: GroupUpdate) => Promise<Group | null>
  deleteGroup: (groupId: string) => Promise<boolean>
  setSelectedGroup: (group: GroupWithMembers | null) => void
  setFilters: (filters: Partial<GroupFilters>) => void
  setSearchQuery: (query: string) => void
  clearFilters: () => void

  // Member actions
  addMember: (groupId: string, clientId: string) => Promise<void>
  addMembers: (groupId: string, clientIds: string[]) => Promise<void>
  removeMember: (groupId: string, clientId: string) => Promise<void>
  getGroupMemberIds: (groupId: string) => Promise<string[]>
}

export const useGroupStore = create<GroupState>((set, get) => ({
  // Initial state
  groups: [],
  selectedGroup: null,
  isLoading: false,
  error: null,
  filters: {},
  searchQuery: '',

  // Fetch all groups with member counts
  fetchGroups: async (providerId: string) => {
    set({ isLoading: true, error: null })
    try {
      const groups = await groupService.getGroupsWithCounts(providerId)
      const { searchQuery, filters } = get()

      // Apply client-side filtering
      let filteredGroups = groups
      if (searchQuery) {
        filteredGroups = filteredGroups.filter(g =>
          g.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }
      if (filters.category) {
        filteredGroups = filteredGroups.filter(g => g.category === filters.category)
      }

      set({ groups: filteredGroups, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch groups',
        isLoading: false,
      })
    }
  },

  // Fetch a single group with members
  fetchGroupById: async (groupId: string) => {
    set({ isLoading: true, error: null })
    try {
      const group = await groupService.getGroupById(groupId)
      set({ selectedGroup: group, isLoading: false })
      return group
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch group',
        isLoading: false,
      })
      return null
    }
  },

  // Create a new group
  createGroup: async (groupData: GroupInsert) => {
    set({ isLoading: true, error: null })
    try {
      const group = await groupService.createGroup(groupData)
      set((state) => ({
        groups: [...state.groups, { ...group, member_count: 0 }],
        isLoading: false,
      }))
      return group
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create group',
        isLoading: false,
      })
      throw error
    }
  },

  // Update a group
  updateGroup: async (groupId: string, updates: GroupUpdate) => {
    set({ isLoading: true, error: null })
    try {
      const updated = await groupService.updateGroup(groupId, updates)
      if (updated) {
        set((state) => ({
          groups: state.groups.map((g) =>
            g.id === groupId ? { ...g, ...updated } : g
          ),
          selectedGroup:
            state.selectedGroup?.id === groupId
              ? { ...state.selectedGroup, ...updated }
              : state.selectedGroup,
          isLoading: false,
        }))
      }
      return updated
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update group',
        isLoading: false,
      })
      throw error
    }
  },

  // Delete a group
  deleteGroup: async (groupId: string) => {
    set({ isLoading: true, error: null })
    try {
      await groupService.deleteGroup(groupId)
      set((state) => ({
        groups: state.groups.filter((g) => g.id !== groupId),
        selectedGroup:
          state.selectedGroup?.id === groupId ? null : state.selectedGroup,
        isLoading: false,
      }))
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete group',
        isLoading: false,
      })
      return false
    }
  },

  // Set selected group
  setSelectedGroup: (group: GroupWithMembers | null) => {
    set({ selectedGroup: group })
  },

  // Set filters
  setFilters: (filters: Partial<GroupFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }))
  },

  // Set search query
  setSearchQuery: (query: string) => {
    set({ searchQuery: query })
  },

  // Clear all filters
  clearFilters: () => {
    set({ filters: {}, searchQuery: '' })
  },

  // Add a single member to a group
  addMember: async (groupId: string, clientId: string) => {
    set({ isLoading: true, error: null })
    try {
      await groupService.addMember(groupId, clientId)
      // Refresh the group to get updated members
      const group = await groupService.getGroupById(groupId)
      set((state) => ({
        selectedGroup: group,
        groups: state.groups.map((g) =>
          g.id === groupId ? { ...g, member_count: group?.member_count || 0 } : g
        ),
        isLoading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add member',
        isLoading: false,
      })
      throw error
    }
  },

  // Add multiple members to a group
  addMembers: async (groupId: string, clientIds: string[]) => {
    set({ isLoading: true, error: null })
    try {
      await groupService.addMembers(groupId, clientIds)
      // Refresh the group to get updated members
      const group = await groupService.getGroupById(groupId)
      set((state) => ({
        selectedGroup: group,
        groups: state.groups.map((g) =>
          g.id === groupId ? { ...g, member_count: group?.member_count || 0 } : g
        ),
        isLoading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add members',
        isLoading: false,
      })
      throw error
    }
  },

  // Remove a member from a group
  removeMember: async (groupId: string, clientId: string) => {
    set({ isLoading: true, error: null })
    try {
      await groupService.removeMember(groupId, clientId)
      // Update local state
      set((state) => {
        const updatedSelectedGroup = state.selectedGroup?.id === groupId
          ? {
              ...state.selectedGroup,
              members: state.selectedGroup.members.filter(m => m.client_id !== clientId),
              member_count: state.selectedGroup.member_count - 1,
            }
          : state.selectedGroup

        return {
          selectedGroup: updatedSelectedGroup,
          groups: state.groups.map((g) =>
            g.id === groupId ? { ...g, member_count: Math.max(0, g.member_count - 1) } : g
          ),
          isLoading: false,
        }
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove member',
        isLoading: false,
      })
      throw error
    }
  },

  // Get member IDs for a group (useful for bulk assignment)
  getGroupMemberIds: async (groupId: string) => {
    try {
      return await groupService.getGroupMemberIds(groupId)
    } catch (error) {
      console.error('Failed to get group member IDs:', error)
      return []
    }
  },
}))
